import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button, ComboboxItem, Menu } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  IconCheck,
  IconClipboardData,
  IconCreditCard,
  IconExternalLink,
  IconEye,
  IconHistory,
  IconLink,
  IconPencil,
  IconX,
} from "@tabler/icons-react";

import { AnalysisPrescriptionDocumentsModal } from "@pages/Appointments/components/AnalysisPrescriptionDocumentsModal";
import { AnalysisTestsModal } from "@pages/Appointments/components/AnalysisTestsModal";
import { CloseFollowUpModal } from "@pages/Appointments/components/CloseFollowUpModal";
import { EditFollowUpModal } from "@pages/Appointments/components/EditFollowUpModal";
import { EditHomecareAppointmentModal } from "@pages/Appointments/components/EditHomecareAppointmentModal";
import { EditLabAppointmentModal } from "@pages/Appointments/components/EditLabAppointmentModal";
import { EditTeleconsultationAppointmentModal } from "@pages/Appointments/components/EditTeleconsultationAppointmentModal";
import { NotificationHistoryModal } from "@pages/Appointments/components/NotificationHistoryModal";
import { StyledTableControls } from "@pages/Appointments/components/TableControls/TableControls.styled";

import { CancelAppointmentModal } from "@components/CancelAppointmentModal";
import { LastUpdatedTimestamp } from "@components/LastUpdated";
import { PatientNotesModal } from "@components/PatientNotesModal";
import { PermissionGate } from "@components/PermissionGate";
import { RejectAppointmentModal } from "@components/RejectAppointmentModal";

import { api } from "@utils/api";
import {
  showLoadingNotification,
  showSuccessNotification,
} from "@utils/showNotification";

import { ROLES } from "@constants/index";

import { useAcceptAppointment } from "@hooks/mutations/appointment/useAcceptAppointment";
import { useCreateConsultationWithAppointment } from "@hooks/mutations/consultation/useCreateConsultationWithAppointment";
import { useQueryErrorHandling } from "@hooks/useQueryErrorHandling";

import { useAuth } from "@contexts/auth";

import { Appointment } from "@customTypes/appointment";
import { UnifiedScheduleItem } from "@customTypes/schedule";

import { PaymentExceptionsModal } from "./PaymentExceptionsModal";


interface ScheduleTableControlsProps {
  row: UnifiedScheduleItem;
  doctors: ComboboxItem[];
}

export const ScheduleTableControls = ({
  row,
  doctors,
}: ScheduleTableControlsProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { token, roles, can } = useAuth();
  const handleErrors = useQueryErrorHandling();

  // Modal States
  const [
    editHomecareModalOpened,
    { open: editHomecareModalOpen, close: editHomecareModalClose },
  ] = useDisclosure(false);
  const [
    editTeleconsultationModalOpened,
    {
      open: editTeleconsultationModalOpen,
      close: editTeleconsultationModalClose,
    },
  ] = useDisclosure(false);
  const [
    editLabApptModalOpened,
    { open: editLabApptModalOpen, close: editLabApptModalClose },
  ] = useDisclosure(false);
  const [
    editFollowUpModalOpened,
    { open: editFollowUpModalOpen, close: editFollowUpModalClose },
  ] = useDisclosure(false);
  const [
    closeFollowUpModalOpened,
    { open: closeFollowUpModalOpen, close: closeFollowUpModalClose },
  ] = useDisclosure(false);
  const [
    patientNotesModalOpened,
    { open: openPatientNotesModal, close: closePatientNotesModal },
  ] = useDisclosure(false);
  const [
    analysisDocumentsModalOpened,
    { open: openAnalysisDocumentsModal, close: closeAnalysisDocumentsModal },
  ] = useDisclosure(false);
  const [
    analysisTestsModalOpened,
    { open: openAnalysisTestsModal, close: closeAnalysisTestsModal },
  ] = useDisclosure(false);
  const [
    notificationHistoryModalOpened,
    { open: openNotificationHistoryModal, close: closeNotificationHistoryModal },
  ] = useDisclosure(false);
  const [
    cancelModalOpened,
    { open: openCancelModal, close: closeCancelModal },
  ] = useDisclosure(false);
  const [
    rejectModalOpened,
    { open: openRejectModal, close: closeRejectModal },
  ] = useDisclosure(false);
  const [
    paymentExceptionsOpened,
    { open: openPaymentExceptions, close: closePaymentExceptions },
  ] = useDisclosure(false);

  // Data State
  const [activeFullAppointment, setActiveFullAppointment] =
    useState<Appointment | null>(null);
  const [isFetching, setIsFetching] = useState(false);

  // Mutations
  const {
    mutate: createConsultationWithAppointment,
    isLoading: isCreateConsultationWithAppointmentLoading,
  } = useCreateConsultationWithAppointment();

  const { mutate: acceptAppointment, isLoading: isAcceptAppointmentLoading } =
    useAcceptAppointment();

  // Manual fetch helper to bypass interceptor issues and ensure token is sent
  const fetchFullAppointment = async () => {
    if (activeFullAppointment) return activeFullAppointment;

    setIsFetching(true);
    const appId = row.originalAppointmentId || row.id;

    try {
      // We explicitly pass the Authorization header to guarantee it's sent
      const res = await api.get(`appointments/${appId}/`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      // Data normalization (Manual Adapter)
      const data = res.data;
      const {
        _id,
        department,
        medium,
        doctor_assigned,
        status,
        category,
        details,
        is_follow_up,
        created_at,
        updated_at,
        payment_transaction_details,
        appointment_date,
        consultation_id,
        patient_id,
        priority,
        promo_code,
        files,
      } = data;

      const transformed: Appointment = {
        id: _id,
        medium,
        department,
        doctorAssigned: {
          id: doctor_assigned?._id,
          fullName: doctor_assigned?.full_name,
        },
        consultationId: consultation_id ?? "",
        patientId: patient_id,
        status,
        category,
        createdAt: new Date(created_at),
        updatedAt: new Date(updated_at),
        isFollowUp: is_follow_up,
        appointmentDate: new Date(appointment_date),
        priority: priority ? priority : null,
        details: {
          ...details,
          // Handle specific date conversions for legacy modals
          serviceDate: details.service_date ? new Date(details.service_date) : undefined,
          t_service_date: details.t_service_date ? new Date(details.t_service_date) : undefined,
          l_service_date: details.l_service_date ? new Date(details.l_service_date) : undefined,
          firstName: details.first_name,
          surname: details.surname,
          phoneNumber: details.phone_number,
          serviceType: details.service_type || details.t_service_type || details.l_service_type,
          serviceTypeId: details.service_type_id || details.t_service_type_id || details.l_service_type_id,
          // Safe lab fields
          serviceTypeIds: details?.l_service_type_ids?.map((item: any) => ({
            label: item.type,
            value: item._id,
          })) || [],
        } as any,
        paymentTransaction: {
          amountPaid: payment_transaction_details?.amount_paid ?? 0,
          date: new Date(payment_transaction_details?.date),
        },
        promoCode: promo_code || "",
        files,
      };

      setActiveFullAppointment(transformed);
      return transformed;
    } catch (error: any) {
      console.error("[Manual Fetch Error]", error);
      handleErrors(error, "query");
      return null;
    } finally {
      setIsFetching(false);
    }
  };

  const handleEditClick = async () => {
    if (row.status === "follow_up") {
      editFollowUpModalOpen();
    } else {
      const data = await fetchFullAppointment();
      if (data) {
        if (row.category === "teleconsultation") {
          editTeleconsultationModalOpen();
        } else if (row.category === "homecare") {
          editHomecareModalOpen();
        } else if (row.category === "lab") {
          editLabApptModalOpen();
        }
      }
    }
  };

  const handleTestsClick = async () => {
    const data = await fetchFullAppointment();
    if (data) openAnalysisTestsModal();
  };

  const handleDocsClick = async () => {
    const data = await fetchFullAppointment();
    if (data) openAnalysisDocumentsModal();
  };

  const handleCopyLink = () => {
    if (row.paymentLink) {
      navigator.clipboard.writeText(row.paymentLink);
      showSuccessNotification({
        title: "Success",
        message: "Payment link copied",
      });
    }
  };

  return (
    <>
      <StyledTableControls className="table-controls">
        {activeFullAppointment && (
          <>
            <EditHomecareAppointmentModal
              opened={editHomecareModalOpened}
              close={() => {
                editHomecareModalClose();
                setActiveFullAppointment(null);
              }}
              selectedRow={activeFullAppointment}
              doctors={doctors}
              variant={row.status === "pending" ? "incoming" : "booked"}
            />
            <EditTeleconsultationAppointmentModal
              opened={editTeleconsultationModalOpened}
              close={() => {
                editTeleconsultationModalClose();
                setActiveFullAppointment(null);
              }}
              selectedRow={activeFullAppointment}
              doctors={doctors}
              variant={row.status === "pending" ? "incoming" : "booked"}
            />
            <EditLabAppointmentModal
              opened={editLabApptModalOpened}
              close={() => {
                editLabApptModalClose();
                setActiveFullAppointment(null);
              }}
              selectedRow={activeFullAppointment}
              doctors={doctors}
              variant={row.status === "pending" ? "incoming" : "booked"}
            />
            <AnalysisPrescriptionDocumentsModal
              files={(activeFullAppointment.files as string[]) ?? []}
              opened={analysisDocumentsModalOpened}
              close={() => {
                closeAnalysisDocumentsModal();
                setActiveFullAppointment(null);
              }}
            />
            <AnalysisTestsModal
              selectedRow={activeFullAppointment}
              opened={analysisTestsModalOpened}
              close={() => {
                closeAnalysisTestsModal();
                setActiveFullAppointment(null);
              }}
            />
          </>
        )}

        {/* Follow-up Modals: Use row data */}
        <EditFollowUpModal
          opened={editFollowUpModalOpened}
          close={editFollowUpModalClose}
          followUp={{
            id: row.id,
            followUpDate: row.date,
            reason: row.service,
          }}
        />

        <CloseFollowUpModal
          opened={closeFollowUpModalOpened}
          close={closeFollowUpModalClose}
          followUp={{
            id: row.id,
            patientId: row.patientId,
            billStatus: row.billStatus,
          }}
        />

        <PatientNotesModal
          opened={patientNotesModalOpened}
          close={closePatientNotesModal}
          patientId={row.patientId}
          appointmentId={row.originalAppointmentId || row.id}
          followUp={{ cId: row.cId ?? "" }}
        />

        <NotificationHistoryModal
          opened={notificationHistoryModalOpened}
          close={closeNotificationHistoryModal}
          appointmentId={row.originalAppointmentId || row.id}
        />

        <CancelAppointmentModal
          opened={cancelModalOpened}
          close={closeCancelModal}
          appointmentId={row.originalAppointmentId || row.id}
        />
        <RejectAppointmentModal
          opened={rejectModalOpened}
          close={closeRejectModal}
          appointmentId={row.originalAppointmentId || row.id}
        />

        {can("access.manage") && row.type === "appointment" && (
          <PaymentExceptionsModal
            opened={paymentExceptionsOpened}
            close={closePaymentExceptions}
            appointmentId={row.originalAppointmentId || row.id}
          />
        )}

        <Menu withArrow position="bottom-end" withinPortal>
          <Menu.Target>
            <Button
              variant="outline"
              size="compact-sm"
              loading={
                isCreateConsultationWithAppointmentLoading ||
                isAcceptAppointmentLoading ||
                isFetching
              }>
              {t("action")}
            </Button>
          </Menu.Target>
          <Menu.Dropdown>
            {row.status === "follow_up" ? (
              <>
                <PermissionGate permission="appointments.edit"><Menu.Item
                  leftSection={<IconPencil size={16} stroke={1.5} />}
                  onClick={editFollowUpModalOpen}>
                  {t("edit-follow-up")}
                </Menu.Item></PermissionGate>
                <Menu.Item
                  disabled={row.billStatus !== "paid"}
                  color="primary"
                  leftSection={<IconX size={16} stroke={1.5} />}
                  onClick={closeFollowUpModalOpen}>
                  {t("close-follow-up")}
                </Menu.Item>
              </>
            ) : (
              <>
                {row.status === "pending" && (
                  <>
                    <PermissionGate permission="appointments.assign"><Menu.Item
                      leftSection={<IconCheck size={16} stroke={1.5} />}
                      onClick={() => {
                        const id = showLoadingNotification({});
                        acceptAppointment(
                          { appointmentId: row.id },
                          {
                            onSuccess: () => {
                              showSuccessNotification({
                                id,
                                title: "success",
                                message: "book-appointment-success",
                              });
                            },
                            onError: (res: any) => {
                              handleErrors(res, "mutation", id);
                            },
                          }
                        );
                      }}>
                      {t("accept")}
                    </Menu.Item></PermissionGate>
                    <PermissionGate permission="appointments.assign"><Menu.Item
                      color="red"
                      leftSection={<IconX size={16} stroke={1.5} />}
                      onClick={openRejectModal}>
                      {t("reject")}
                    </Menu.Item></PermissionGate>
                  </>
                )}

                {row.category === "lab" && (
                  <>
                    <Menu.Item
                      leftSection={<IconEye size={16} stroke={1.5} />}
                      onClick={handleTestsClick}>
                      {t("view-analysis-tests")}
                    </Menu.Item>
                    <Menu.Item
                      leftSection={<IconEye size={16} stroke={1.5} />}
                      onClick={handleDocsClick}>
                      {t("view-analysis-documents")}
                    </Menu.Item>
                  </>
                )}
                <PermissionGate permission="appointments.edit"><Menu.Item
                  leftSection={<IconPencil size={16} stroke={1.5} />}
                  onClick={handleEditClick}>
                  {t("edit-appointment")}
                </Menu.Item></PermissionGate>
              </>
            )}

            <Menu.Item
              leftSection={<IconClipboardData size={16} stroke={1.5} />}
              onClick={openPatientNotesModal}>
              {t("open-patient-notes")}
            </Menu.Item>

            <Menu.Item
              leftSection={<IconHistory size={16} stroke={1.5} />}
              onClick={openNotificationHistoryModal}>
              {t("view-notification-logs")}
            </Menu.Item>

            {can("access.manage") && row.type === "appointment" && (
              <Menu.Item
                leftSection={<IconCreditCard size={16} stroke={1.5} />}
                onClick={openPaymentExceptions}>
                {t("payment-activity")}
              </Menu.Item>
            )}

            {row.paymentLink && (
              <Menu.Item
                leftSection={<IconLink size={16} stroke={1.5} />}
                onClick={handleCopyLink}>
                {t("copy-payment-link") || "Copy Payment Link"}
              </Menu.Item>
            )}

            {row.category !== "lab" && row.status !== "pending" && (
              <Menu.Item
                leftSection={<IconExternalLink size={16} stroke={1.5} />}
                onClick={() => {
                  if (row.consultationId) {
                    navigate(
                      `/patients/${row.patientId}/consultation/general/${row.consultationId}`
                    );
                  } else if (row.type === "appointment") {
                    const id = showLoadingNotification({});
                    createConsultationWithAppointment(
                      {
                        appointmentId: row.id,
                        patientId: row.patientId!,
                      },
                      {
                        onError: res => handleErrors(res, "mutation", id),
                        onSuccess: res => {
                          showSuccessNotification({
                            id,
                            title: "success",
                            message: "create-consultation-success",
                          });
                          setTimeout(() => {
                            navigate(
                              `/patients/${row.patientId}/consultation/general/${res.data.consultation_id}`
                            );
                          }, 500);
                        },
                      }
                    );
                  }
                }}>
                {!row.consultationId
                  ? t("create-consultation")
                  : t("view-consultation")}
              </Menu.Item>
            )}

            {row.status === "booked" && (
              <>
                <Menu.Divider />
                <Menu.Item
                  leftSection={<IconX size={16} stroke={1.5} />}
                  color="red"
                  onClick={openCancelModal}>
                  {t("cancel-appointment")}
                </Menu.Item>
              </>
            )}
          </Menu.Dropdown>
        </Menu>
      </StyledTableControls>
      {row.updatedAt && (
        <LastUpdatedTimestamp
          updatedDate={row.updatedAt}
          className="last-updated"
        />
      )}
    </>
  );
};
